import { useCallback, useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";
import { z } from "zod";
import { safeLocalStorage } from "@helpers/storage";

export type SideNavLevel = "workspace" | "org";

const STORAGE_KEYS: Record<SideNavLevel, string> = {
    workspace: "kick:side-nav:workspace:sections:v2",
    org: "kick:side-nav:org:sections",
};

/** A collapsible section and the routes that live inside it. */
export interface SideNavSectionRoutes {
    label: string;
    /** `matchPath` patterns that open the section, e.g. `/transactions/*`. */
    paths: readonly string[];
    /** Starts closed until the user or a route inside it opens it. */
    isCollapsedByDefault?: boolean;
}

interface SavedSections {
    opened: ReadonlySet<string>;
    closed: ReadonlySet<string>;
}

const EMPTY_SAVED_SECTIONS: SavedSections = {
    opened: new Set(),
    closed: new Set(),
};

const savedSectionsSchema = z.union([
    z.object({ opened: z.array(z.string()), closed: z.array(z.string()) }),
    // v1 stored only the labels the user closed.
    z.array(z.string()).transform((closed) => ({ opened: [], closed })),
]);

const readSavedSections = (storageKey: string): SavedSections => {
    const stored = safeLocalStorage.getItem(storageKey);
    if (stored === null) {
        return EMPTY_SAVED_SECTIONS;
    }
    try {
        const parsed = savedSectionsSchema.safeParse(JSON.parse(stored));
        return parsed.success
            ? {
                  opened: new Set(parsed.data.opened),
                  closed: new Set(parsed.data.closed),
              }
            : EMPTY_SAVED_SECTIONS;
    } catch {
        return EMPTY_SAVED_SECTIONS;
    }
};

const getLabelsMatching = (
    sections: readonly SideNavSectionRoutes[],
    pathname: string,
) =>
    sections
        .filter((section) =>
            section.paths.some((path) => matchPath(path, pathname) !== null),
        )
        .map((section) => section.label);

const withOpened = (
    saved: SavedSections,
    labels: readonly string[],
): SavedSections => {
    const openedLabels = new Set(labels);
    return {
        opened: new Set([...saved.opened, ...labels]),
        closed: new Set(
            [...saved.closed].filter((label) => !openedLabels.has(label)),
        ),
    };
};

const without = (labels: ReadonlySet<string>, label: string) =>
    new Set([...labels].filter((other) => other !== label));

const withClosed = (saved: SavedSections, label: string): SavedSections => ({
    opened: without(saved.opened, label),
    closed: new Set([...saved.closed, label]),
});

/**
 * Open state of a level's `SideNavSection`s. A section the user opened or
 * closed keeps that choice, remembered in localStorage under the level's
 * `STORAGE_KEYS` entry; any other section is open unless its config is
 * `isCollapsedByDefault`. A route landing inside a section opens it until the
 * user closes it or the nav remounts, without remembering it. Spread
 * `sectionProps(label)` onto each `SideNavSection`.
 */
export const useSideNavSections = ({
    level,
    sections,
}: {
    level: SideNavLevel;
    sections: readonly SideNavSectionRoutes[];
}) => {
    const storageKey = STORAGE_KEYS[level];
    const { pathname } = useLocation();
    const [saved, setSaved] = useState<SavedSections>(() =>
        readSavedSections(storageKey),
    );
    const [routeOpened, setRouteOpened] = useState<ReadonlySet<string>>(
        () => new Set(getLabelsMatching(sections, pathname)),
    );
    const [previousPathname, setPreviousPathname] = useState(pathname);

    if (pathname !== previousPathname) {
        setPreviousPathname(pathname);
        const matchingLabels = getLabelsMatching(sections, pathname);
        if (matchingLabels.some((label) => !routeOpened.has(label))) {
            setRouteOpened(new Set([...routeOpened, ...matchingLabels]));
        }
    }

    useEffect(() => {
        safeLocalStorage.setItem(
            storageKey,
            JSON.stringify({
                opened: [...saved.opened],
                closed: [...saved.closed],
            }),
        );
    }, [saved, storageKey]);

    const sectionProps = useCallback(
        (label: string) => {
            const isCollapsedByDefault = sections.some(
                (section) =>
                    section.label === label &&
                    section.isCollapsedByDefault === true,
            );
            return {
                label,
                isOpen:
                    saved.opened.has(label) ||
                    routeOpened.has(label) ||
                    (!saved.closed.has(label) && !isCollapsedByDefault),
                onOpenChange: (shouldOpen: boolean) => {
                    setSaved((current) =>
                        shouldOpen
                            ? withOpened(current, [label])
                            : withClosed(current, label),
                    );
                    if (!shouldOpen) {
                        setRouteOpened((current) => without(current, label));
                    }
                },
            };
        },
        [routeOpened, saved, sections],
    );

    return { sectionProps };
};
